/**
 * Marketing figures and booking link of the public site (B3-home).
 *
 * One source for every figure the site shows: the meta description, the
 * stats band, the services copy and the social kit used to quote different
 * numbers (+10 000 / +50 000 calls, 95 % / 4.9/5). The values below are the
 * ones the homepage displayed; changing a figure here changes it everywhere.
 *
 * The public site books meetings through one provider only, the HubSpot
 * Meetings page below. The platform `calendly_url` setting (/AdminCalendly)
 * never fed this page (DUP-1).
 */
export const MARKETING_FIGURES = {
  /** Calls made, shown as "+50 000". */
  calls: 50_000,
  /** Meetings booked, shown as "+5 000". */
  meetings: 5_000,
  /** Average delay before the first meeting, in hours. */
  firstMeetingHours: 48,
  /** Customer satisfaction, out of 5. */
  satisfaction: 4.9,
} as const;

/**
 * The only booking provider of the public site (HubSpot Meetings, embedded).
 * QA-W6-14: this page answered 404 on 2026-11-01; the widget falls back to
 * CONTACT_EMAIL until the product gives the real meetings link.
 */
export const BOOKING_URL = 'https://meetings-eu1.hubspot.com/pouhyet?embed=true';

/** The public contact address, the booking widget's fallback. */
export const CONTACT_EMAIL = 'contact@revcall.fr';

/**
 * Background video of the homepage hero. QA-W6-14: answered 404 on
 * 2026-11-01; the hero falls back to a still background until the product
 * gives a real file.
 */
export const HERO_VIDEO_URL = 'https://videos.pexels.com/video-files/3252118/3252118-uhd_2560_1440_25fps.mp4';

/** How long the booking page has to announce itself before the fallback shows. */
export const BOOKING_LOAD_TIMEOUT_MS = 12_000;

export interface FormattedFigures {
  calls: string;
  meetings: string;
  firstMeeting: string;
  satisfaction: string;
}

/** The figures as displayed, formatted for the reader's language. */
export function formatMarketingFigures(locale: string): FormattedFigures {
  const integer = new Intl.NumberFormat(locale);
  const decimal = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return {
    calls: `+${integer.format(MARKETING_FIGURES.calls)}`,
    meetings: `+${integer.format(MARKETING_FIGURES.meetings)}`,
    firstMeeting: `${MARKETING_FIGURES.firstMeetingHours}h`,
    satisfaction: `${decimal.format(MARKETING_FIGURES.satisfaction)}/5`,
  };
}
